import { animated, config, useSpring } from '@react-spring/web'
import React, { useEffect, useState } from 'react'
import { Actions } from '~/actions'
import Button from '~/components/Button'
import { CartIcon, Close } from '~/components/Icons'
import { useTranslations } from '~/components/Locale'
import { Portal } from '~/components/utils'
import type { ShopifyHeaderSettings } from '~/types'
import { cn, isShop, useDispatch, useScrollbarWidth, useSelector, useWindowSize } from '~/utils'
import { Cart } from './Cart'
import './CartDrawer.less'

interface Props {
	settings: ShopifyHeaderSettings
}

export const CartDrawer: React.FC<Props> = ({ settings }) => {
	const dispatch = useDispatch()
	const set = (b: boolean) => dispatch(Actions.openCart(b))
	const t = useTranslations('react.actions')
	const screen = useWindowSize()
	const scrollWidth = useScrollbarWidth()
	const cartWidth = screen.width < 460 ? screen.width : 400

	const { open, lines } = useSelector(s => s.cart)
	const itemCount = lines?.reduce((a, b) => a + b.quantity, 0) || 0
	const [alwaysShow, setAlwaysShow] = useState(itemCount > 0)

	useEffect(() => {
		if (itemCount > 0) setAlwaysShow(true)
	}, [itemCount])

	const { right, opacity } = useSpring({
		right: open ? scrollWidth : -cartWidth,
		opacity: open ? 1 : 0,
		config: {
			...config.default,
			restVelocity: open ? 0.01 : 10
		}
	})

	return (
		<>
			{/* Always show cart in header on shop pages or if there are items in the cart*/}
			{isShop() || alwaysShow ? (
				<div>
					<button
						className="button btn--square btn--hover-scale site-header__cart relative leading-[2.2]"
						onClick={() => set(true)}
					>
						<CartIcon />
						{itemCount > 0 && <span className="absolute right-2 top-4 h-4 w-4 rounded-full bg-red-600" />}
					</button>
				</div>
			) : (
				<Button size="md" href={settings.shop_link} className="btn--shop float-right px-4 py-3 lg:px-8">
					{t?.buy_now}
				</Button>
			)}
			<Portal open={open} alwaysRender>
				<animated.div
					className="cart__drawer portal"
					style={{ right: right.to(Math.round), width: `${cartWidth}px` }}
				>
					<Cart />
					<button className="btn btn--clear cart__close" onClick={() => set(false)}>
						<Close />
					</button>
				</animated.div>
				<animated.div
					className={cn('cart__backdrop', { 'pointer-events-none': !open })}
					style={{ opacity: opacity.to(n => n.toFixed(2)) }}
					onClick={() => set(false)}
				/>
			</Portal>
		</>
	)
}
